/**
 * Flattens a nested object into a single-level object with dot-separated keys.
 * @param {object} obj - The nested object to flatten.
 * @param {string} [parentKey] - The parent key for the current level.
 * @param {Record<string, string>} result - The result object.
 * @returns {Record<string, string>} The flattened object.
 */
export function flattenObject(
  obj: any,
  parentKey: string = '',
  result: Record<string, string> = {},
): Record<string, string> {
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const isNestedObject =
      typeof obj[key] === 'object' &&
      obj[key] !== null &&
      !Array.isArray(obj[key]);

    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (isNestedObject && !key.includes('.')) {
      flattenObject(obj[key], newKey, result);
    } else {
      result[newKey] = obj[key];
    }
  }

  return result;
}

export const setAs = (
  entity: any,
  defaultValue: any = null,
  type = 'string',
) => {
  const value = typeof entity === 'undefined' ? defaultValue : entity;
  return type === 'string' || type === 'number'
    ? value
    : type === 'boolean'
      ? !!value
      : value;
};

/**
 * Creates a replacer function that handles circular references and other complex objects.
 * @returns {(_key: string, value: any) => any} The replacer function.
 */
export const createSafeReplacer = (): ((_key: string, value: any) => any) => {
  const seen = new WeakSet<object>();
  return function (_key: string, value: any) {
    const t = typeof value;
    if (t === 'function' || t === 'symbol') return undefined;
    if (value && t === 'object') {
      // Skip DOM nodes and other host objects by checking the constructor name
      const ctorName = value.constructor?.name;
      if (
        ctorName &&
        ctorName !== 'Object' &&
        ctorName !== 'Array' &&
        ctorName !== 'Date'
      ) {
        // Attempt shallow plain projection for Maps/Sets, otherwise drop
        if (value instanceof Map) return Object.fromEntries(value);
        if (value instanceof Set) return Array.from(value.values());
        if (value instanceof Date) return value.toISOString();
        // Drop complex instances (e.g. Firebase containers, class instances)
        return undefined;
      }
      if (seen.has(value)) return undefined;
      seen.add(value);
    }
    return value;
  };
};
